import React from "react";
import { forEach, sortBy, groupBy, map, filter } from "lodash";
import { Icon, List } from "semantic-ui-react";
import Text from "@Components/Text";

const strId = (v) => {
	return v == null ? "" : String(v);
};

// Normaliza option.action (string/objeto/arreglo) y devuelve {name, type, value}, usando stageId como valor por defecto.
const getActionMeta = (opt) => {
	const a = opt?.action;

	if (typeof a === "string") {
		return { name: a, type: null, value: opt?.stageId ?? null };
	}

	if (Array.isArray(a) && a.length) {
		const p = a[0] ?? {};
		let value = p.value ?? opt?.stageId ?? null;
		// Si value es un objeto con stageId, extraer el stageId
		if (value && typeof value === "object" && value.stageId) {
			value = value.stageId;
		}
		return {
			name: p.name ?? "",
			type: p.type ?? null,
			value,
		};
	}

	if (a && typeof a === "object") {
		let value = a.value ?? opt?.stageId ?? null;
		// Si value es un objeto con stageId, extraer el stageId
		if (value && typeof value === "object" && value.stageId) {
			value = value.stageId;
		}
		return {
			name: a.name ?? "",
			type: a.type ?? null,
			value,
		};
	}

	return { name: "", type: null, value: opt?.stageId ?? null };
};

const isMoveToMenu = (opt) => {
	const m = getActionMeta(opt);
	const link = opt?.stageId ?? m.value;
	return (m.type === "menu" || m.name === "Mover a Menú" || opt?.action?.function === "moveToMenu") && link != null;
};

const isCloseAction = (opt) => {
	const m = getActionMeta(opt);
	return m.type === "close" || m.name === "Cerrar Sesión";
};

const isBackAction = (opt) => {
	if (isCloseAction(opt)) return false;
	const label = (opt?.label || "").toLowerCase();
	return (
		label === "volver atrás" || label === "volver atras" || label === "volver" || label === "menú anterior" || label === "menu anterior"
	);
};

const isHomeAction = (opt) => {
	if (isCloseAction(opt)) return false;
	const label = (opt?.label || "").toLowerCase();
	return (label === "menú principal" || label === "menu principal") && isMoveToMenu(opt);
};

const getLinkedStageId = (opt) => {
	const m = getActionMeta(opt);
	return opt?.stageId ?? m.value ?? null;
};

// Genera una lista plana y ordenada de items (stage/option) desde los stages, normalizando IDs y adjuntando metadatos para el historial.
export function processStages(stages) {
	const items = [];
	forEach(stages, (stage, stageIndex) => {
		const optionCount = stage.options ? stage.options.length : 0;
		items.push({
			id: `stage-${strId(stage.id)}`,
			type: "stage",
			content: `${stage.name || "Sin nombre"}`,
			stage,
			order: stageIndex,
			optionCount,
		});
		if (stage.options && stage.options.length > 0) {
			forEach(stage.options, (option, optionIndex) => {
				items.push({
					id: `option-${strId(stage.id)}-${strId(option.id)}`,
					type: "option",
					content: option.label || `Opción ${optionIndex + 1}`,
					option,
					stageIndex,
					order: stageIndex + (optionIndex + 1) * 0.1,
					parentStageId: strId(stage.id),
					optionIndex,
				});
			});
		}
	});
	return sortBy(items, "order");
}

// Renderiza el estado vacío del historial cuando no hay menús configurados.
export function renderEmptyState(styles) {
	return (
		<div className={styles.emptyHistory}>
			<Icon name="info circle" />
			<Text>No hay Menús configurados</Text>
		</div>
	);
}

// Renderiza un ítem del historial (menú u opción) con sus íconos y controles, gestionando expansión y mostrando enlaces/relaciones.
export function renderHistoryItem({
	item,
	stages,
	expandedOptionsState,
	setExpandedOptions,
	optionsCount = 0,
	isExpanded = false,
	onToggle = null,
	styles,
}) {
	let linkedStageName = null;
	if (item.type === "stage" && item.stage.stageId) {
		const linkedStage = stages.find((s) => {
			return strId(s.id) === strId(item.stage.stageId);
		});
		if (linkedStage) linkedStageName = linkedStage.name || `Menú ${linkedStage.id}`;
	}

	let linkedFromNames = [];
	if (item.type === "stage") {
		linkedFromNames = stages
			.filter((s) => {
				return s.stageId === item.stage.id;
			})
			.map((s) => {
				return s.name || `Menú ${s.id}`;
			});
	}

	// const m = item.type === "option" ? getActionMeta(item.option) : null;
	const isBack = item.type === "option" && isBackAction(item.option);
	const isHome = item.type === "option" && isHomeAction(item.option);
	const isClose = item.type === "option" && isCloseAction(item.option);
	const mover = item.type === "option" && isMoveToMenu(item.option) && !isClose && !isBack && !isHome;

	let optionHasLink = false;
	let linkedStageType = null;
	if (mover) {
		const linkedId = getLinkedStageId(item.option);
		const linkedStage = stages.find((s) => {
			return strId(s.id) === strId(linkedId);
		});
		if (linkedStage) {
			linkedStageType = linkedStage.type;
			if (linkedStage.options && linkedStage.options.length > 0) {
				optionHasLink = true;
			}
		}
	}

	return (
		<List.Item key={item.id} className={styles.historyItem} data-type={item.type}>
			<List.Content className={styles.historyContentFlex}>
				<div className={styles.historyItemRow}>
					{item.type === "stage" && <List.Icon name="list" className={styles.stageIcon} />}

					{item.type === "option" && optionHasLink && (
						<List.Icon
							name={expandedOptionsState[item.id] ? "angle down" : "angle right"}
							className={`${styles.optionIcon} ${styles.expandIcon}`}
							onClick={() => {
								return setExpandedOptions((prev) => {
									return { ...prev, [item.id]: !prev[item.id] };
								});
							}}
							title={expandedOptionsState[item.id] ? "Colapsar subopciones" : "Ver subopciones"}
						/>
					)}

					{item.type === "option" && !optionHasLink && (
						<List.Icon
							name={
								isHome
									? "home"
									: isBack
									? "reply"
									: isClose
									? "power off"
									: linkedStageType === "buttonUri"
									? "linkify"
									: linkedStageType === "buttonPhone"
									? "phone"
									: linkedStageType === "buttonFlow"
									? "file alternate outline"
									: "circle"
							}
							className={`${styles.optionIcon} ${styles.dotIcon} ${isClose ? styles.dotIconRed : ""} ${
								isBack || isHome ? styles.dotIconBlue : ""
							} ${["buttonUri", "buttonPhone", "buttonFlow"].includes(linkedStageType) ? styles.dotIconPurple : ""}`}
						/>
					)}

					<List.Header className={styles.historyItemHeader}>{item.content}</List.Header>

					{item.type === "stage" && optionsCount > 0 && (
						<Icon
							name={isExpanded ? "angle up" : "angle down"}
							onClick={onToggle}
							className={styles.collapseIcon}
							title={isExpanded ? "Colapsar opciones" : "Ver todas las opciones"}
						/>
					)}
				</div>

				{item.type === "stage" && linkedStageName && (
					<List.Description className={`${styles.historyItemDescription} ${styles.noMarginTop}`}>
						Enlazado con: {linkedStageName}
					</List.Description>
				)}
				{item.type === "stage" && linkedFromNames.length > 0 && (
					<List.Description className={styles.historyItemDescription}>Enlazado desde: {linkedFromNames.join(", ")}</List.Description>
				)}
			</List.Content>
		</List.Item>
	);
}

// Renderiza recursivamente las subopciones de un menú enlazado, evitando ciclos y manejando expansión/colapso e iconos.
export function renderChildStageOptions({
	stageId,
	stages,
	renderedStages = new Set(),
	parentOptionId = null,
	expandedOptionsState,
	setExpandedOptions,
	styles,
}) {
	const sId = strId(stageId);
	if (renderedStages.has(sId)) return null;
	renderedStages.add(sId);

	const stage = stages.find((s) => {
		return strId(s.id) === sId;
	});
	if (!stage || !stage.options) return null;

	return (
		<div className={styles.childStageItem} key={`child-options-${sId}-${parentOptionId || ""}`}>
			{stage.options.map((opt, idx) => {
				const optionId = `option-${strId(stage.id)}-${strId(opt.id)}`;
				const close = isCloseAction(opt);
				const back = isBackAction(opt);
				const home = isHomeAction(opt);
				const mover = isMoveToMenu(opt) && !close && !back && !home;
				const linkedId = getLinkedStageId(opt);

				let showDot = false;
				let linkedStageType = null;
				if (mover) {
					const linkedStage = stages.find((s) => {
						return strId(s.id) === strId(linkedId);
					});
					if (linkedStage) linkedStageType = linkedStage.type;
					if (!linkedStage || !linkedStage.options || linkedStage.options.length === 0) showDot = true;
				}

				const optionItem = (
					<div className={styles.childStageOptionRow} key={optionId}>
						{mover && !showDot ? (
							<List.Icon
								name={expandedOptionsState[optionId] ? "angle down" : "angle right"}
								className={`${styles.optionIcon} ${styles.expandIcon}`}
								onClick={() => {
									return setExpandedOptions((prev) => {
										return { ...prev, [optionId]: !prev[optionId] };
									});
								}}
								title={expandedOptionsState[optionId] ? "Colapsar subopciones" : "Ver subopciones"}
							/>
						) : (
							<List.Icon
								name={
									home
										? "home"
										: back
										? "reply"
										: close
										? "power off"
										: linkedStageType === "buttonUri"
										? "linkify"
										: linkedStageType === "buttonPhone"
										? "phone"
										: linkedStageType === "buttonFlow"
										? "file alternate outline"
										: "circle outline"
								}
								className={`${styles.optionIcon} ${styles.dotIcon} ${close ? styles.dotIconRed : ""} ${
									back || home ? styles.dotIconBlue : ""
								} ${["buttonUri", "buttonPhone", "buttonFlow"].includes(linkedStageType) ? styles.dotIconPurple : ""}`}
							/>
						)}
						<Text className={styles.historyItemHeader}>{opt.label || `Opción ${idx + 1}`}</Text>
					</div>
				);

				if (mover && !showDot) {
					return [
						optionItem,
						expandedOptionsState[optionId] &&
							renderChildStageOptions({
								stageId: linkedId,
								stages,
								renderedStages: new Set(renderedStages),
								parentOptionId: optionId,
								expandedOptionsState,
								setExpandedOptions,
								styles,
							}),
					];
				}

				return optionItem;
			})}
		</div>
	);
}

// Renderiza el historial agrupado por menú, ocultando menús destino y gestionando expansión/colapso de opciones y submenús enlazados.
export function renderHistoryList({ historyItems, stages, expandedMenus, expandedOptions, setExpandedMenus, setExpandedOptions, styles }) {
	const grouped = groupBy(historyItems, (item) => {
		return item.type === "stage" ? strId(item.stage.id) : strId(item.parentStageId);
	});

	// Identificar sub-menús (enlaces hacia adelante o hacia atrás) para ocultarlos de la raíz.
	// 1. Buscamos todos los IDs que están siendo enlazados por algún otro menú.
	const linkedStageIds = new Set();
	forEach(grouped, (items) => {
		const stageItem = items.find((i) => {
			return i.type === "stage";
		});
		const currentStageIdStr = stageItem ? strId(stageItem.stage.id) : null;

		forEach(items, (opt) => {
			if (opt.option && isMoveToMenu(opt.option)) {
				const linkId = getLinkedStageId(opt.option);
				if (linkId != null) {
					const targetIdStr = strId(linkId);
					// Solo es un "hijo" si quien lo enlaza es un menú distinto (no es un self-link)
					if (targetIdStr !== currentStageIdStr) {
						linkedStageIds.add(targetIdStr);
					}
				}
			}
		});
	});

	// 2. Ocultamos de la raíz los menús que son submenús, excepto el primero que es el punto de entrada.
	const childStageIds = new Set();

	const firstId = stages.length > 0 ? strId(stages[0].id) : null;

	linkedStageIds.forEach((id) => {
		// Siempre conservamos al menos el primer menú (punto de entrada) en la raíz
		if (id !== firstId) childStageIds.add(id);
	});

	return (
		<List divided relaxed>
			{stages.map((s) => {
				const stageIdStr = strId(s.id);
				if (childStageIds.has(stageIdStr)) return null;

				const items = grouped[stageIdStr];
				if (!items) return null;

				const stage = items.find((item) => {
					return item.type === "stage";
				});
				const options = filter(items, (item) => {
					return item.type === "option";
				});
				if (!stage) return null;

				const showCollapse = options.length > 0;
				const isExpanded = !!expandedMenus[stageIdStr];
				const visibleOptions = showCollapse && !isExpanded ? [] : options;

				return (
					<div key={stage.id} className={styles.menuBlock}>
						{renderHistoryItem({
							item: stage,
							stages,
							expandedOptionsState: expandedOptions,
							setExpandedOptions,
							optionsCount: options.length,
							isExpanded,
							onToggle: () => {
								return setExpandedMenus((prev) => {
									return { ...prev, [stageIdStr]: !isExpanded };
								});
							},
							styles,
						})}
						{map(visibleOptions, (opt) => {
							const renderOption = renderHistoryItem({
								item: opt,
								stages,
								expandedOptionsState: expandedOptions,
								setExpandedOptions,
								styles,
							});
							if (opt.option && isMoveToMenu(opt.option)) {
								return (
									<div key={opt.id} className={styles.optionBlock}>
										{renderOption}
										{expandedOptions[opt.id] &&
											renderChildStageOptions({
												stageId: getLinkedStageId(opt.option),
												stages,
												renderedStages: new Set([stageIdStr]),
												expandedOptionsState: expandedOptions,
												setExpandedOptions,
												styles,
											})}
									</div>
								);
							}
							return renderOption;
						})}
					</div>
				);
			})}
		</List>
	);
}

// Alterna la expansión global del historial, abriendo todos los menús y subopciones enlazables o colapsándolos.
export function handleGlobalToggle({ expandedAll, setExpandedAll, stages, setExpandedMenus, setExpandedOptions }) {
	if (expandedAll) {
		setExpandedMenus({});
		setExpandedOptions({});
	} else {
		const allMenus = {};
		const allOptions = {};
		stages.forEach((stage) => {
			const sId = strId(stage.id);
			allMenus[sId] = true;
			if (stage.options) {
				stage.options.forEach((option) => {
					const optionId = `option-${sId}-${strId(option.id)}`;
					if (isMoveToMenu(option)) {
						allOptions[optionId] = true;
					}
				});
			}
		});
		setExpandedMenus(allMenus);
		setExpandedOptions(allOptions);
	}
	setExpandedAll(!expandedAll);
}
